import { Injectable } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Language } from '../config/app.constants';

interface UrlStructure {
  path: string;
  queryParams: Record<string, string>;
  fragment: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class LocalizeRouter {

  constructor(
    private _localize: LocalizeRouterService
  ) { }

  public route(value: any): any {
    // Return null if the input value is falsy
    if (!value) {
      return null;
    }

    // Check if the value is a string
    if (typeof value === 'string') {
      // If it's a URL, process it accordingly
      if (this.isUrl(value)) {
        return this.url(value); // Handle as URL
      }
      return this.fragment(value); // Handle as fragment
    }

    // Check if the value is an array
    if (Array.isArray(value)) {
      return this.params(value); // Process as parameters array
    }

    // Handle other types of values as query parameters
    return this.queryParams(value);
  }

  private url(url: string): string {
    // Parse the input URL into its components (path, query parameters, and fragment)
    const urlStructure = this.parseUrl(url);
    let localizeUrl = '';

    // Translate the path of the URL using the localization service
    localizeUrl = `${localizeUrl}${this.translate(urlStructure.path)}`;

    // Check if there are any query parameters in the parsed URL
    if (Object.keys(urlStructure.queryParams).length) {
      localizeUrl = `${localizeUrl}?`;

      // Construct the query string from localized query parameters
      const queryString = Object.entries(this.queryParams(urlStructure.queryParams))
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

      localizeUrl = `${localizeUrl}${queryString}`;
    } else {
      // Set a default language parameter if no query parameters exist
      const lang = this._localize.parser.currentLang === Language.FRENCH ? `?langue=${Language.FRENCH}` :
        `?lang=${Language.ENGLISH}`;
      localizeUrl = `${localizeUrl}${lang}`;
    }

    // Check if there is a fragment in the parsed URL and append it if present
    if (urlStructure.fragment) {
      const fragment = this.fragment(urlStructure.fragment); // Translate fragment
      localizeUrl = `${localizeUrl}#${fragment}`;
    }

    return localizeUrl; // Return the fully constructed localized URL
  }

  private params(params: string[]): (string | any[])[] {
    // Translate each parameter in the provided array
    return params.map(param => this.translate(param));
  }

  private queryParams(queryParams: Record<string, any>): Record<string, any> {
    const localizedQueryParams: Record<string, any> = {};

    // Set the current language in the localized query parameters
    const lang = this.translate('lang');
    localizedQueryParams[lang] = this._localize.parser.currentLang === Language.FRENCH ? Language.FRENCH : Language.ENGLISH;

    // Iterate over each entry in queryParams and translate them
    for (const [prop, value] of Object.entries(queryParams)) {
      const key = this.translate(prop); // Translate parameter key
      localizedQueryParams[key] = value; // Assign original value to translated key
    }

    return localizedQueryParams; // Return the object with localized query parameters
  }

  private fragment(fragment: string): string | any[] {
    // Translate the fragment string before returning it
    return this.translate(fragment);
  }

  private translate(value: string): string {
    // Use localization service to translate the given route value and convert to string
    return this._localize.translateRoute(value).toString();
  }

  private parseUrl(url: string): UrlStructure {
    // Create a URL object; base URL is needed for relative URLs
    const urlObj = new URL(url, 'https://dummybase.com');

    // Extract path, query parameters, and fragment from the URL object
    const path = urlObj.pathname;

    const queryParams: Record<string, string> = {};

    urlObj.searchParams.forEach((value, key) => {
      queryParams[key] = value; // Populate query parameters object from search params
    });

    const fragment = urlObj.hash ? urlObj.hash.substring(1) : null; // Remove '#' from fragment

    return {
      path,
      queryParams,
      fragment,
    };
  }

  private isUrl(value: string): boolean {
    const pathSeparator = '/';
    const queryParamSeparator = '?';
    const fragmentSeparator = '#';

    // Check for presence of path, query, or fragment separators to determine if it's a URL
    if (value.includes(pathSeparator) || value.includes(queryParamSeparator) || value.includes(fragmentSeparator)) {
      return true;
    }

    return false; // Return false if none of the separators are found
  }
}